import { Position } from '@statsbomb/parachute-types';
import { SYSTEM_WIDE_USER_ID } from '@/consts/userConfigs';
import { RadarData } from '@/types/radar';
import { UserConfigRadarTemplate } from '@/types/userConfigs';
import { snakeToCamel } from '@/utils/queries';
import type { PositionMinutes, ProfileInfo } from '@statsbomb/kitbag-ai-summary';

export const isGkPosition = (position: string | undefined): boolean => position === Position.GK;

export const formatRadarValue = (value: number, isPercentage: boolean) => value * (isPercentage ? 100 : 1);

// StatsBomb (system-wide) profiles have translatable titles keyed under `templates.<title>`;
// user-owned and shared profiles use their raw, user-entered title as-is.
export const getRadarTemplateDisplayName = (template: UserConfigRadarTemplate, t: (key: string) => string): string => {
  const { title } = template.definition.titleSettings;
  return template.hudlUserId === SYSTEM_WIDE_USER_ID ? t(`templates.${title}`) : title;
};

// Maps a selected radar template to the ProfileInfo the AI summary agent consumes: a display name
// plus the profile's own metric keys and positions (the evaluation lens, not the player's data).
// positionMinutes is the player's per-position minutes, nested here because it's only meaningful
// alongside a selected profile; omitted when empty so it isn't sent on the wire. Metric keys the user
// may not see (hiddenMetricKeys) are dropped so they aren't leaked to the agent via the profile.
export const toProfileInfo = (
  template: UserConfigRadarTemplate,
  t: (key: string) => string,
  positionMinutes: PositionMinutes[],
  hiddenMetricKeys: ReadonlySet<string>,
): ProfileInfo => ({
  name: getRadarTemplateDisplayName(template, t),
  metrics: template.definition.metrics.map(metric => metric.key).filter(key => !hiddenMetricKeys.has(key)),
  positions: template.definition.positions ?? [],
  ...(positionMinutes.length ? { positionMinutes } : {}),
});

export const toRadarVisData = (t: (key: string) => string, items: RadarData[]) =>
  items.map(d => ({ ...d, label: t(`${snakeToCamel(d.key)}.name`) }));

export const toPercentileRadarData = (data: RadarData[], percentiles: Record<string, number>): RadarData[] =>
  data.map(item => ({
    ...item,
    value: percentiles[item.key] ?? 0,
    minValue: 0,
    maxValue: 100,
    decimalPlaces: 0,
  }));
